import { ApolloType } from "@/enum/apollo.enum";
import { ListSourceType } from "@/enum/list.enum";

import { LiEventInclusionStatus } from "@/enum/scrape-enum";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { CustomEventType } from "@/enum/custom-event.enum";
import { ScrapeLevel } from "@/enum/job.enum";

import { List } from "../schema/list";

export type Step =
	| "details"
	| "source"
	| "filter"
	| "settings"
	| "events"
	| "copywriting"
	| "signals";

export const detailSteps = (isSequencerActive: boolean) =>
	isSequencerActive
		? ["source", "filter", "details", "copywriting", "settings"]
		: ["source", "filter", "details", "settings"];

const specialSteps = ["copywriting", "settings"];

export const detailStepsWithoutFilter = (isSequencerActive: boolean) =>
	isSequencerActive
		? ["source", "details", "copywriting", "settings"]
		: ["source", "details", "settings"];

export const normalSteps = (isSequencerActive: boolean) =>
	isSequencerActive ? ["source", "copywriting", "settings"] : ["source", "settings"];

export const reducedSteps = ["settings"];

// The standalone "filter" step edits the legacy single filter / semantic-search query, which only
// smart lists created from the Contacts module carry. Catalog-created smart lists filter per-source
// (smart_list_source rows) in the Source step, so the filter step is hidden unless a filter is set.
export const smartListSteps = (isSequencerActive: boolean, hasFilter: boolean) => {
	const steps = isSequencerActive
		? ["source", "filter", "copywriting", "settings"]
		: ["source", "filter", "settings"];

	return hasFilter ? steps : steps.filter((step) => step !== "filter");
};

export const liEventSteps = ["source", "events", "filter", "settings"];

export const customEventSteps = ["source", "filter", "details", "settings"];

// "details" is the tracker's Find person step: an optional automatic search for a company signal
// that names no known person (PREC-4399).
export const trackerSteps = (isSequencerActive: boolean) =>
	isSequencerActive
		? ["source", "signals", "details", "copywriting", "settings"]
		: ["source", "signals", "details", "settings"];

export const isDetailsNeeded = (data: List) =>
	(data?.sourceType === ListSourceType.MAPS && data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT) ||
	(data?.sourceType === ListSourceType.JOBS && data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT) ||
	(data?.sourceType === ListSourceType.PARALLEL_AI &&
		data?.scrapeLevel === ScrapeLevel.GET_ENRICHMENT) ||
	(data?.sourceType === ListSourceType.CSV_ENRICHMENTS &&
		data?.entityType === CsvEnrichmentType.ORGANIZATION) ||
	(data?.sourceType === ListSourceType.CUSTOM_EVENTS &&
		data?.entityType === CustomEventType.ORGANIZATION) ||
	data?.sourceType === ListSourceType.FUNDING_ROUNDS ||
	data?.sourceType === ListSourceType.TRACKER;

export const getAvailableSteps = (
	isDetailsNeeded: boolean,
	list?: List,
	isSequencerActive?: boolean
) => {
	if (list?.specialType !== null) {
		return specialSteps;
	}

	// LI_CONNECTIONS lists are auto-populated from the user's synced LinkedIn connections, so
	// there is no source to configure — skip the "source" step (Campaigns + Settings only).
	if (
		list?.sourceType === ListSourceType.EMPTY ||
		list?.sourceType === ListSourceType.LI_CONNECTIONS
	) {
		return specialSteps;
	}

	if (list?.sourceType === ListSourceType.TRACKER) {
		return trackerSteps(isSequencerActive ?? false);
	}

	if (list?.sourceType === ListSourceType.SMART_LIST) {
		const hasFilter = !!(list?.smartList?.filter || list?.smartList?.semanticSearchProspects);

		return smartListSteps(isSequencerActive ?? false, hasFilter);
	}

	if (isDetailsNeeded && list.sourceType === ListSourceType.PARALLEL_AI) {
		return detailStepsWithoutFilter(isSequencerActive ?? false);
	}

	if (isDetailsNeeded) {
		return detailSteps(isSequencerActive ?? false);
	}

	return normalSteps(isSequencerActive ?? false);
};

export const getDoneSteps = (data: List, forceSteps?: Step[]) => {
	const doneSteps: Step[] = [];

	if (
		(data?.scrapeLevel && data.apollos?.[0]?.config?.enrichmentFilterConfig) ||
		(data?.sourceType === ListSourceType.TRACKER &&
			data.apollos?.find((apollo) => apollo.type === ApolloType.SOURCE)?.config
				?.findPersonOnSignal !== undefined)
	) {
		doneSteps.push("details");
	}

	const amountOfSources =
		(data?.maps?.length || 0) +
		(data?.jobs?.length || 0) +
		(data?.liCompanyActivity?.length || 0) +
		(data?.csvEnrichments?.length || 0) +
		(data?.liEvents?.length || 0) +
		(data?.liPeople?.length || 0) +
		(data?.customEvents?.length || 0) +
		(data?.liPosts?.length || 0) +
		(data?.liProfile?.length || 0) +
		(data?.liProfileVisits?.length || 0) +
		(data?.fundingMa?.length || 0) +
		(data?.liSn?.length || 0) +
		(data?.swarms?.length || 0) +
		(data?.parallelAi?.length || 0) +
		(data?.companyEnriches?.length || 0) +
		(data?.trackers?.flatMap((t) => t.trackerFiles || []).length || 0) +
		(data?.smartList?.sources?.length || 0);

	if (data?.apollos?.[0]?.config?.firstSaved) {
		doneSteps.push("filter");
	}

	// Smart lists don't use Apollo; the filter step is "done" once filters or a semantic
	// search query have been saved on the smart-list relation.
	if (
		data?.sourceType === ListSourceType.SMART_LIST &&
		(data?.smartList?.filter || data?.smartList?.semanticSearchProspects)
	) {
		doneSteps.push("filter");
	}

	if (data?.campaignConfig) {
		doneSteps.push("copywriting");
	}

	const csvEnrichmentFindPersonAtCompanyIsDone =
		data.entityType &&
		[CsvEnrichmentType.ORGANIZATION].includes(data.entityType as CsvEnrichmentType) &&
		data?.apollos?.some((apollo) => {
			return !!apollo.config?.enrichmentFilterConfig;
		});

	const fundingRoundFindPersonAtCompanyIsDone = data?.apollos?.some((apollo) => {
		return !!apollo.config?.enrichmentFilterConfig;
	});

	const csvEnrichmentPeopleEnrichmentIsDone =
		data.entityType && [CsvEnrichmentType.PERSON].includes(data.entityType as CsvEnrichmentType);

	const csvEnrichmentFindPersonAtCompanyTitleProvidedIsDone =
		data.entityType &&
		[CsvEnrichmentType.PERSON_BY_TITLE].includes(data.entityType as CsvEnrichmentType) &&
		data.enrichmentType;

	if (
		(data.sourceType === ListSourceType.CSV_ENRICHMENTS ||
			data.sourceType === ListSourceType.CUSTOM_EVENTS) &&
		(csvEnrichmentFindPersonAtCompanyIsDone ||
			csvEnrichmentPeopleEnrichmentIsDone ||
			csvEnrichmentFindPersonAtCompanyTitleProvidedIsDone)
	) {
		doneSteps.push("details");
	}

	if (data.sourceType === ListSourceType.FUNDING_ROUNDS && fundingRoundFindPersonAtCompanyIsDone) {
		doneSteps.push("details");
	}

	if (
		data.sourceType === ListSourceType.LI_EVENTS &&
		data?.liEvents?.some((event) =>
			event.liEventEntries.some(
				(liEventEntry) => liEventEntry.inclusionStatus === LiEventInclusionStatus.ADDED
			)
		)
	) {
		doneSteps.push("events");
	}

	if (data?.lemlists?.length) {
		doneSteps.push("settings");
	}

	if (amountOfSources > 0) {
		doneSteps.push("source");
	}

	// Signal config is done when the list-signal-config record is created (has an id)
	if (data?.signalConfig?.id) {
		doneSteps.push("signals");
	}

	if (forceSteps && forceSteps?.length > 0) {
		forceSteps.forEach((step) => {
			if (!doneSteps.includes(step)) {
				doneSteps.push(step);
			}
		});
	}

	return doneSteps;
};
